import {
  CalendarDate,
  CalendarDateTime,
  parseDate,
  parseDateTime,
  parseTime,
  Time,
  ZonedDateTime,
} from "@internationalized/date";
import { format, getUnixTime, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { DateRangePickerProps } from "ds/ui";

export type CalendarDateInput =
  | CalendarDate
  | CalendarDateTime
  | ZonedDateTime
  | null;

export const toCalendarDate = (
  value: number | Date | CalendarDate | CalendarDateTime | null | undefined,
  granularity?: string,
): CalendarDate | CalendarDateTime | null | undefined => {
  if (value === null) return null;
  if (value === undefined || value === 0) return undefined;

  const isUnixTimestampSeconds = typeof value === "number";

  // If it's a unix timestamp, convert it to a Date object
  if (isUnixTimestampSeconds) {
    value = new Date((value as number) * 1000);
  }

  // If it's already a CalendarDate or CalendarDateTime, return it
  if (
    typeof value === "object" &&
    "year" in value &&
    "month" in value &&
    "day" in value
  ) {
    return value;
  }

  if (isValid(value)) {
    const isTimeGranularity =
      granularity === "minute" || granularity === "second";
    const formatString = isTimeGranularity
      ? "yyyy-MM-dd'T'HH:mm:ss"
      : "yyyy-MM-dd";

    // Day-only: use the user's local calendar. UTC extraction shifts the shown
    // day for positive offsets (e.g. click the 8th → field shows the 7th).
    const formatted =
      isUnixTimestampSeconds && isTimeGranularity
        ? formatInTimeZone(value, "UTC", formatString)
        : format(value, formatString);
    return isTimeGranularity ? parseDateTime(formatted) : parseDate(formatted);
  }

  // Invalid value
  return undefined;
};

export const toTime = (
  value: Date | Time | null | undefined,
): Time | undefined => {
  if (!value) return undefined;

  // If it's already a Time object, return it
  if ("hour" in value && "minute" in value) {
    return value;
  }

  // If it's a Date object, convert it
  const formattedTime = format(value, "HH:mm:ss");
  return parseTime(formattedTime);
};

export const convertCalendarDateToUnix = (
  calendarDate: CalendarDateInput,
  granularity: DateRangePickerProps["granularity"] = "day",
): number | null => {
  if (!calendarDate) return null;

  if (granularity === "minute" || granularity === "second") {
    const dateTime = calendarDate as CalendarDateTime;
    return getUnixTime(
      new Date(
        dateTime.year,
        dateTime.month - 1,
        dateTime.day,
        dateTime.hour,
        dateTime.minute,
        dateTime.second ?? 0,
      ),
    );
  }

  // Use noon local time so the calendar day stays stable across all UTC offsets
  // (UTC-12 to UTC+12), even if the backend normalises timestamps to midnight UTC.
  return getUnixTime(
    new Date(calendarDate.year, calendarDate.month - 1, calendarDate.day, 12, 0, 0),
  );
};

export const convertCalendarRangeToUnixRange = (
  range: {
    end: CalendarDateInput;
    start: CalendarDateInput;
  } | null,
  granularity: DateRangePickerProps["granularity"] = "day",
): { end: number | null; start: number | null } => {
  if (!range) {
    return { end: null, start: null };
  }

  return {
    end: convertCalendarDateToUnix(range.end, granularity),
    start: convertCalendarDateToUnix(range.start, granularity),
  };
};
